import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { SkeletonKPIRow } from '../../components/ui/Skeleton';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { PageActions } from '../../components/layout/PageActions';
import { useUrlState } from '../../hooks/useUrlState';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtEur, fmtDate } from '../../lib/utils';
import { adminOverviewQueryFor, qontoStatusQuery, canWriteAdmin, plurale, type AdminOverview } from './adminApi';
import { useAdminActions } from './useAdminActions';
import { PanoramicaTab } from './PanoramicaTab';
import { IncassiTab } from './IncassiTab';
import { UsciteTab } from './UsciteTab';
import { FornitoriTab } from './FornitoriTab';
import { FiscoTab } from './FiscoTab';
import { MovimentiTab } from './MovimentiTab';
import { ChiuseQuiTab } from './ChiuseQuiTab';
import { RiconciliazioneTab } from './RiconciliazioneTab';
import { DocumentsSection } from '../invoices/DocumentsSection';
import './admin.css';

/**
 * Amministrazione — l'unica sezione dei soldi (ha assorbito la Contabilità:
 * l'emissione dei documenti vive nella tab "Documenti").
 *   page-ed-hd  → titolo + sottotitolo (stato Qonto) + storico + "+ Nuovo" + Azioni
 *   stat-band   → Liquidità / Da incassare / Da pagare / IVA scaduta (click = tab)
 *   page-tabs   → Panoramica | Fatture | Uscite | Fornitori | Fisco | Movimenti | Chiuse qui | Documenti
 *
 * Storico: di default si guarda dalla data di taglio in poi (1/1/2026, la tiene
 * OrgSettings.adminCutoffDate). Niente è stato cancellato: l'interruttore
 * "Anni scorsi" nell'intestazione rimette in lista tutto, in ogni tab.
 *
 * Stato in URL: tab · storico (+ i filtri di ogni tab: q, status, type, band…)
 *   es. /admin?tab=incassi&status=aperte · /admin?tab=documenti&type=quote
 */

type Tab = 'panoramica' | 'incassi' | 'uscite' | 'fornitori' | 'fisco' | 'movimenti' | 'riconciliazione' | 'chiuse-qui' | 'documenti';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'panoramica', label: 'Panoramica' },
  // "Fatture" e non "Incassi" (Lorenzo, 19/09/2026): qui dentro ci sono le
  // fatture emesse, incassate e no, quindi il nome giusto è quello del documento
  { key: 'incassi',    label: 'Fatture' },
  { key: 'uscite',     label: 'Uscite' },
  { key: 'fornitori',  label: 'Fornitori' },
  { key: 'fisco',      label: 'Fisco' },
  { key: 'movimenti',  label: 'Movimenti' },
  // Dove la banca e i documenti si guardano in faccia: le rate che risultano
  // aperte ma in banca sono state pagate, e viceversa (vedi RiconciliazioneTab).
  { key: 'riconciliazione', label: 'Da riconciliare' },
  // Le rate saldate qui che su FattureInCloud risultano ancora aperte: è quello
  // che un giorno va riportato al commercialista (vedi ChiuseQuiTab).
  { key: 'chiuse-qui', label: 'Chiuse qui' },
  { key: 'documenti',  label: 'Documenti' },
];

export function AdminPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [tabParam, setTab] = useUrlState<string>('tab', 'panoramica');
  const tab: Tab = (TABS.some((t) => t.key === tabParam) ? tabParam : 'panoramica') as Tab;
  const [storicoParam, setStorico] = useUrlState<string>('storico', '');
  const showHistory = storicoParam === '1';
  const isMobile = useIsMobile();

  const { data: ov, isLoading } = useQuery(adminOverviewQueryFor(showHistory));
  const { data: qonto } = useQuery(qontoStatusQuery);
  const { syncQonto, syncFic } = useAdminActions();

  const goTab = (t: Tab) => setTab(t);
  // Lo stato Qonto arriva dalle Impostazioni; se quella route manca, dall'overview
  const qontoConnected = qonto?.connected ?? ov?.sync?.qonto?.connected ?? false;
  const lastQonto = ov?.sync?.qonto?.lastAt ?? qonto?.lastSyncAt ?? null;

  return (
    // `adm-page` non veste niente: è il recinto di admin.css, così una regola
    // scritta per queste nove schede non può finire su un'altra pagina.
    <div className="page adm-page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Amministrazione</h1>
          <div className="ed-sub">
            Soldi in cassa, cosa entra, cosa esce, cosa dobbiamo e i documenti che emettiamo.
            {' '}
            {!showHistory && ov?.history?.cutoffDate && <span className="mute">Dal {fmtDate(ov.history.cutoffDate)} in poi. </span>}
            {qontoConnected
              ? <Chip tone="ok" dot><Icon name="landmark" size={10} /> Qonto{lastQonto ? ` · aggiornato ${fmtDateTime(lastQonto)}` : ''}</Chip>
              : <span className="text-warn">Qonto non collegato — <a className="underline cursor-pointer" onClick={() => navigate('/settings')}>collega</a> per saldi e movimenti.</span>}
          </div>
        </div>
        {/* Sul telefono questi comandi salgono nella barra in alto, accanto alla
            lente: sotto i 768px l'intestazione non ha più titolo né sottotitolo
            e da soli si prendevano una riga di 60px sopra il primo numero.
            Sul computer PageActions rende esattamente il div di prima. */}
        <PageActions className="flex items-center gap-2 pb-1 adm-head-actions">
          {/* In barra l'interruttore diventa una voce del menu: due bottoni
              accanto al titolo si mangerebbero il nome della sezione, e "Anni
              scorsi" ridotto a una sveglia muta non lo capirebbe nessuno. */}
          {!isMobile && <HistorySwitch on={showHistory} cutoffDate={ov?.history?.cutoffDate ?? null} onChange={(v) => setStorico(v ? '1' : '')} />}
          {canWrite && <NewAdminButton />}
          <ActionsMenu
            variant="ghost"
            label="Azioni"
            items={[
              ...(isMobile ? [{
                key: 'storico',
                label: showHistory ? 'Torna al presente' : 'Mostra anche gli anni scorsi',
                icon: 'clock' as IconName,
                onClick: () => setStorico(showHistory ? '' : '1'),
              }] : []),
              { key: 'sync-qonto', label: syncQonto.isPending ? 'Aggiornamento Qonto…' : 'Aggiorna Qonto', icon: 'refresh', disabled: syncQonto.isPending || !qontoConnected, onClick: () => syncQonto.mutate() },
              { key: 'sync-fic', label: syncFic.isPending ? 'Aggiornamento FiC…' : 'Aggiorna FattureInCloud', icon: 'invoice', disabled: syncFic.isPending, onClick: () => syncFic.mutate() },
            ]}
          />
        </PageActions>
      </div>

      {isLoading && !ov ? (
        <SkeletonKPIRow count={4} withIcon={false} />
      ) : (
        <StatBand ov={ov} onTab={goTab} />
      )}

      {showHistory && (
        <div className="adm-note">
          <Icon name="clock" size={12} className="mute" />
          <span>
            Stai guardando <strong>anche gli anni scorsi</strong>: i numeri qui sopra e in tutte le tab comprendono
            fatture vecchie che nessuno sta più chiedendo.{' '}
            <a className="underline cursor-pointer" onClick={() => setStorico('')}>Torna al presente</a>
          </span>
        </div>
      )}

      <div className="page-tabs">
        {TABS.map((t) => (
          <div key={t.key} className="page-tab" aria-selected={tab === t.key} onClick={() => goTab(t.key)}>
            {t.label}
            {t.key === 'incassi' && ov != null && <span className="c">{ov.fatture.openCount}</span>}
            {/* Fornitori distinti, non documenti: la tab raggruppa per fornitore. */}
            {t.key === 'fornitori' && ov != null && (ov.fornitori.vendorCount ?? ov.fornitori.count) > 0 && (
              <span className="c">{ov.fornitori.vendorCount ?? ov.fornitori.count}</span>
            )}
            {/* Il pallino sta sulla tab che risolve il problema, non sulla Panoramica
                che lo mostra soltanto: da lì si conferma o si scarta. E conta TUTTE
                le proposte, non solo quelle chiudibili con un clic, altrimenti
                sparisce proprio quando resta solo roba da guardare. */}
            {t.key === 'riconciliazione' && ov != null && (ov.fatture.daRiconciliare?.proposte ?? ov.fatture.toReview.length) > 0 && (
              <span className="c">{ov.fatture.daRiconciliare?.proposte ?? ov.fatture.toReview.length}</span>
            )}
          </div>
        ))}
      </div>

      {tab === 'panoramica' && <PanoramicaTab ov={ov} loading={isLoading} canWrite={canWrite} onTab={goTab} showHistory={showHistory} />}
      {tab === 'incassi'    && <IncassiTab canWrite={canWrite} showHistory={showHistory} />}
      {tab === 'uscite'     && <UsciteTab canWrite={canWrite} showHistory={showHistory} />}
      {tab === 'fornitori'  && <FornitoriTab canWrite={canWrite} showHistory={showHistory} />}
      {tab === 'fisco'      && <FiscoTab ov={ov} canWrite={canWrite} />}
      {tab === 'movimenti'  && <MovimentiTab canWrite={canWrite} showHistory={showHistory} />}
      {tab === 'riconciliazione' && <RiconciliazioneTab canWrite={canWrite} />}
      {tab === 'chiuse-qui' && <ChiuseQuiTab />}
      {/* Documenti: la lista che era la Contabilità (permesso FATTURAZIONE, gestito dentro) */}
      {tab === 'documenti'  && <DocumentsSection showHistory={showHistory} />}
    </div>
  );
}

/**
 * Interruttore "Anni scorsi": lo storico non è stato cancellato, è solo fuori
 * dai conti finché non si accende questo. Spento mostra da quando si guarda.
 */
function HistorySwitch({ on, cutoffDate, onChange }: { on: boolean; cutoffDate: string | null; onChange: (v: boolean) => void }) {
  const since = cutoffDate ? fmtDate(cutoffDate) : null;
  return (
    <button
      className={on ? 'btn btn-sm is-active' : 'btn btn-sm btn-ghost'}
      aria-pressed={on}
      title={on ? 'Torna a guardare solo il presente' : `Rimetti in lista i documenti prima del ${since ?? 'taglio'}`}
      onClick={() => onChange(!on)}
    >
      <Icon name="clock" size={10} /> Anni scorsi
    </button>
  );
}

/** "17/09/2026 alle 10:32" per l'ultima sync. */
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return sameDay ? `alle ${hm}` : `il ${fmtDate(d)}`;
}

// ════════════════════════════════════════════════════════════════════
// Stat-band: 4 numeri nudi, cliccabili verso la tab giusta
// ════════════════════════════════════════════════════════════════════
function StatBand({ ov, onTab }: { ov: AdminOverview | undefined; onTab: (t: Tab) => void }) {
  const liq = ov?.liquidity ?? { bank: 0, cash: 0, total: 0 };
  const overdueCount = ov?.fatture.overdueCount ?? 0;
  const overdueAmount = ov?.fatture.overdueAmount ?? 0;
  // `open` del mese include lo scaduto (roll-forward): nel sottotitolo si separa
  // Fornitori, ricorrenti e contanti del mese: il debito col fisco sta nella
  // sua colonna (IVA scaduta), sommarlo qui lo farebbe vedere due volte.
  const toPay = ov?.uscite.thisMonth.openNoTax ?? ov?.uscite.thisMonth.open ?? 0;
  const toPayOverdue = ov?.uscite.thisMonth.overdueNoTax ?? ov?.uscite.thisMonth.overdue ?? 0;
  // In evidenza va lo SCADUTO: sommarci i trimestri non ancora scaduti gonfiava
  // il numero (113.897 invece di 103.422) e lo rendeva irriconoscibile.
  const ivaOverdue = ov?.iva.overdueTotal ?? ov?.iva.residualTotal ?? 0;
  const ivaUpcoming = ov?.iva.upcomingTotal ?? 0;
  const ivaStima = (ov?.iva.byYear ?? []).some((y) => y.source === 'STIMA');
  return (
    <div className="stat-band">
      <StatCell label="Liquidità" value={liq.total} sub={`di cui contanti € ${fmtEur(liq.cash)}`} onClick={() => onTab('panoramica')} />
      <StatCell
        label="Da incassare"
        value={ov?.fatture.openAmount ?? 0}
        sub={overdueCount > 0 ? `${plurale(overdueCount, 'scaduta', 'scadute')} per € ${fmtEur(overdueAmount)}` : `${plurale(ov?.fatture.openCount ?? 0, 'rata aperta', 'rate aperte')}`}
        subAlert={overdueCount > 0}
        onClick={() => onTab('incassi')}
      />
      <StatCell
        label="Da pagare"
        value={toPay}
        sub={toPayOverdue > 0 ? `di cui € ${fmtEur(toPayOverdue)} già scaduti` : 'fornitori e spese del mese, tasse escluse'}
        subAlert={toPayOverdue > 0}
        onClick={() => onTab('uscite')}
      />
      <StatCell
        label="IVA scaduta"
        value={ivaOverdue}
        sub={ivaUpcoming > 0 ? `+ € ${fmtEur(ivaUpcoming)} non ancora scaduta` : ivaStima ? 'stima, da confermare' : 'confermato'}
        badge={ivaStima ? 'Stima' : undefined}
        onClick={() => onTab('fisco')}
      />
    </div>
  );
}

function StatCell({ label, value, sub, subAlert, badge, onClick }: {
  label: string; value: number; sub: string; subAlert?: boolean; badge?: string; onClick: () => void;
}) {
  return (
    <div className="st-click" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className="st-label">{label}{badge && <span className="chip warn adm-st-badge">{badge}</span>}</div>
      <div className="st-value"><span className="u">€</span><AnimatedNumber value={value} format={fmtEur} /></div>
      <div className={`st-delta${subAlert ? ' text-alert' : ''}`}>{sub}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// "+ Nuovo" dropdown (stesso look di NewDocumentButton in Contabilità)
// ════════════════════════════════════════════════════════════════════
function NewAdminButton() {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);
  const go = (kind: 'newCashEntry' | 'newRecurring' | 'newTax' | 'newPayable', payload?: Record<string, unknown>) => {
    setOpen(false);
    openModal(kind, payload);
  };
  return (
    <div className="relative d-only">
      <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" /> Nuovo <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] rounded-[var(--r-card)] border border-line bg-white shadow-lg overflow-hidden" style={{ minWidth: 320 }}>
            <div className="p-2 border-b border-line">
              <div className="t-micro mb-1.5">Contanti</div>
              <Choice icon="banknote" title="Incasso in contanti" sub="Cliente che paga in cassa" onClick={() => go('newCashEntry', { direction: 'IN' })} />
              <Choice icon="banknote" title="Uscita in contanti" sub="Pulizie, manutenzioni, piccole spese" onClick={() => go('newCashEntry', { direction: 'OUT' })} />
            </div>
            <div className="p-2">
              <div className="t-micro mb-1.5">Impegni</div>
              <Choice icon="repeat" title="Spesa ricorrente" sub="Canone, affitto, abbonamento" onClick={() => go('newRecurring')} />
              <Choice icon="calendarClock" title="Scadenza fiscale" sub="F24, IVA, INPS, rate" onClick={() => go('newTax')} />
              <Choice icon="receipt" title="Fattura ricevuta" sub="Fornitore non ancora su FiC" onClick={() => go('newPayable')} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Choice({ icon, title, sub, onClick }: { icon: IconName; title: string; sub: string; onClick: () => void }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer rounded-[var(--r-ctl)] transition" onClick={onClick}>
      <div className="icon-tile sm"><Icon name={icon} size={16} /></div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="mute text-[12px]">{sub}</div>
      </div>
      <Icon name="arrowRight" size={12} className="mute" />
    </div>
  );
}
